/**
 * An old Leading Analyzer link (`?sp=` of its form, `LeadingAnalyzer/Page/@signals.js`) → the view of "My office network"
 * (CU-869fmjzrh D15, UX T2): its offices, length, websites, dates and filters, on the view by pick-up date. Whatever
 * cannot travel is named (`drop`), and the arrival note says it — never in silence. Pure and alias-free.
 */
import { addDays, daysBetween, fromIso, toIso } from '../../../@services/dates.js'
import { MAX_PICKUP_DAYS } from '../../../@services/ranges.js'
import { CARS } from '../../../@services/cars.js'
import { MAX_CAPTURE_DAYS } from './capture.js'

export const DROP = Object.freeze({
	dropoff: 'Drop off office', currency: 'currency', features: 'the "Show features" filters', lengths: 'its other rental lengths',
	officeGroup: 'its office group (not one of yours)', siteGroup: 'its website group (not one of yours)', pickupCut: 'pick-ups beyond 180 days',
	captureCut: `captures beyond ${MAX_CAPTURE_DAYS} days`, brand: 'its main provider (your brand comes from Settings › Account)',
})
const GROUP_BY = { day: 'day', week: 'week', month: 'month' }

const list = v => (Array.isArray(v) ? v : v === null || v === undefined || v === '' ? [] : [v])
const ids = v => [...new Set(list(v).map(Number).filter(n => Number.isInteger(n) && n > 0))]
const date = v => fromIso(String(v ?? '').replace(/-/g, ''))
const hasValue = v => !(v === null || v === undefined || v === false || v === '' || (Array.isArray(v) && !v.length))

/**
 * `{ params: URLSearchParams string, drop: [codes] }`. `context` = `{ officeGroups: [ids], siteGroups: [ids],
 * currency: account currency id, primaryBrand: id | null }` (`null` lists = unknown: the group is kept and the server
 * resolves it with the owner rule).
 */
export function leadingToView(sp, context = {}) {
	const p = new URLSearchParams()
	const drop = []
	const form = sp && typeof sp === 'object' ? sp : {}
	p.set('v', 'pickup')
	p.set('by', GROUP_BY[form.groupBy] || 'day')
	p.set('me', hasValue(form.siteMainID) ? 'site' : 'brand')

	const og = ids(form.officeGroupID)[0]
	if (og) {
		if (!context.officeGroups || context.officeGroups.includes(og)) p.set('og', String(og))
		else drop.push('officeGroup')
	}
	const offices = ids(form.officePickupID)
	if (!p.has('og') && offices.length) p.set('ofc', offices.join(','))

	// the link's first length; the others are in the lengths view (P3)
	const lors = ids(form.lor).filter(x => x < 60)
	if (lors.length) p.set('lor', String(lors[0]))
	if (lors.length > 1) drop.push('lengths')

	const sg = ids(form.siteGroupID)[0]
	if (sg) {
		if (!context.siteGroups || context.siteGroups.includes(sg)) p.set('sg', String(sg))
		else drop.push('siteGroup')
	}
	const sites = ids(form.siteID)
	if (!p.has('sg') && sites.length) p.set('sites', sites.join(','))

	const tier = ids(form.tiers)[0]
	const providers = ids(form.providerID)
	if (tier) p.set('tier', String(tier))
	else if (providers.length) p.set('providers', providers.join(','))

	const acriss = list(form.acriss).filter(x => typeof x === 'string' && x)
	const categories = list(form.categoryID).filter(x => typeof x === 'string' && x)
	if (acriss.length) { p.set('cars', CARS.ACRISS); p.set('carsIds', acriss.join(',')) }
	else if (categories.length) { p.set('cars', CARS.CATEGORY); p.set('carsIds', categories.join(',')) }

	let from = date(form.startPickupDate), to = date(form.endPickupDate)
	if (from && to && from <= to) {
		if (daysBetween(from, to) + 1 > MAX_PICKUP_DAYS) { to = addDays(from, MAX_PICKUP_DAYS - 1); drop.push('pickupCut') }
		p.set('range', 'fixed'); p.set('from', toIso(from)); p.set('to', toIso(to))
	} else {
		p.set('range', 'next4w')
	}

	let cfrom = date(form.startBookingDate), cto = date(form.endBookingDate)
	if (cfrom && cto && cfrom <= cto) {
		if (daysBetween(cfrom, cto) + 1 > MAX_CAPTURE_DAYS) { cfrom = addDays(cto, -(MAX_CAPTURE_DAYS - 1)); drop.push('captureCut') }
		p.set('cap', 'fixed'); p.set('cfrom', toIso(cfrom)); p.set('cto', toIso(cto))
	} else {
		p.set('cap', 'yday')
	}

	p.set('cmp', 'day')
	p.set('stat', 'min')
	p.set('price', form.isDailyPrice ? 'day' : 'total')

	if (hasValue(form.officeDropoffID)) drop.push('dropoff')
	if (hasValue(form.currency) && context.currency !== undefined && String(form.currency) !== String(context.currency)) drop.push('currency')
	if (['unlimited_km', 'insurance', 'is_prepaid', 'fuelPolicyID', 'automatic', 'in_terminal_pickup', 'webSupplierID'].some(k => hasValue(form[k])) || form.showFeatures) drop.push('features')
	if (hasValue(form.providerMainID) && !hasValue(form.siteMainID) && context.primaryBrand && Number(form.providerMainID) !== Number(context.primaryBrand)) drop.push('brand')

	p.set('from_leading', '1')
	if (drop.length) p.set('drop', drop.join(','))
	return { params: p.toString(), drop }
}

/** The arrival note's "Not carried from the link: …" line, or `null` */
export function droppedText(codes = []) {
	const names = codes.map(c => DROP[c]).filter(Boolean)
	return names.length ? `Not carried from the link: ${names.join(', ')}. ${names.length > 1 ? 'They are' : 'It is'} in Advanced (BI).` : null
}
